import { ComboboxItem, Typography, Button, Form } from '@ui/components';
import { FormAssignTable } from '@shared/components/assign-table';
import { CostCenterFormType } from '@pages/cost-centers/utils';
import { HasPermission } from '@shared/components';
import { useHasPermission } from '@shared/hooks';
import { useTranslation } from 'react-i18next';
import { FC } from 'react';

import { useCostCenterAssignUsers } from './cost-centers-assign-users/use-cost-centers-assign-users';

export const CostCenterAssignForm: FC<{ form: CostCenterFormType; editMode?: boolean }> = ({
  form,
  editMode,
}) => {
  const { t } = useTranslation();
  const hasEditPermission = useHasPermission('COST_CENTER_ASSIGN_USERS', 'WRITE');
  const assignedUsers = form.watch('assignedUsers');
  const { assignTable, assignAllUsers, isGetAllAssignUsersPending } = useCostCenterAssignUsers(
    assignedUsers,
    (data) => {
      form.setValue('assignedUsers', data);
    },
    editMode,
  );

  return (
    <Form {...form}>
      {!editMode && (
        <>
          <Typography variant="labelMediumBold">{t('COST_CENTERS.USERS_MANAGEMENT')}</Typography>
          <Typography variant="labelMediumBook">
            {t('COST_CENTERS.USERS_MANAGEMENT_DESCRIPTION')}
          </Typography>
        </>
      )}
      <HasPermission permissionFeature="COST_CENTER_ASSIGN_USERS" permissionType="WRITE">
        <div className="flex justify-end">
          <Button
            isLoading={isGetAllAssignUsersPending}
            disabled={isGetAllAssignUsersPending}
            onClick={() => assignAllUsers()}
            variant="link"
            type="button"
          >
            {t('COST_CENTERS.USERS_MANAGEMENT_ADD_ALL_USERS')}
          </Button>
        </div>
      </HasPermission>

      <FormAssignTable
        {...assignTable}
        text={{
          removeButtonText: t('COST_CENTERS.USERS_ASSIGN_REMOVE'),
          assignButtonText: t('COST_CENTERS.USERS_ASSIGN_ADD'),
          comboboxPlaceholder: t('COST_CENTERS.USERS_ASSIGN_COMBOBOX_PLACEHOLDER'),
          comboboxNoResults: t('COST_CENTERS.USERS_ASSIGN_NO_RESULT'),
          tableNoResults: t('COST_CENTERS.USERS_ASSIGN_NO_RESULT'),
          searchPlaceholder: t('COST_CENTERS.TABLE_SEARCH_PLACEHOLDER'),
          title: t('COST_CENTERS.USERS_ASSIGN_TABLE_TITLE'),
          selectedRows: (count) =>
            t('COST_CENTERS.USERS_ASSIGN_SELECTED_ROWS', {
              postProcess: 'interval',
              count,
            }),
        }}
        itemTemplate={(item) => (
          <ComboboxItem
            keywords={[item.firstName, item.lastName, item.email ?? '']}
            disabled={assignedUsers?.includes(item.id)}
            className="gap-4 h-12"
            label={item.id}
            value={item.id}
            key={item.id}
          >
            <Typography variant="labelMediumBook">
              {item.firstName} {item.lastName}
            </Typography>
            <Typography variant="labelMediumBook">({item.email})</Typography>
          </ComboboxItem>
        )}
        disabled={!hasEditPermission}
        formControl={form.control}
        name="assignedUsers"
      />
    </Form>
  );
};
